import { useEffect, useState } from 'react'

interface UseTypewriterPlaceholderOptions {
  placeholders: string[]
  typingSpeed?: number
  deletingSpeed?: number
  pauseDuration?: number
}

/**
 * Hook that creates a typewriter effect cycling through multiple placeholder strings.
 * Returns the currently displayed text that can be passed to an input's placeholder prop.
 */
export function useTypewriterPlaceholder({
  placeholders,
  typingSpeed = 30,
  deletingSpeed = 20,
  pauseDuration = 2000
}: UseTypewriterPlaceholderOptions): string {
  const [displayedText, setDisplayedText] = useState('')
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPausing, setIsPausing] = useState(false)

  useEffect(() => {
    if (!placeholders || placeholders.length === 0) return

    const currentFullText = placeholders[currentPlaceholderIndex]

    // Determine typing speed
    let speed = typingSpeed
    if (isDeleting) {
      speed = deletingSpeed
    }
    if (isPausing) {
      speed = pauseDuration
    }

    const handleTyping = () => {
      if (isPausing) {
        setIsPausing(false)
        setIsDeleting(true)
        return
      }

      if (!isDeleting) {
        // Typing
        if (displayedText !== currentFullText) {
          setDisplayedText(currentFullText.substring(0, displayedText.length + 1))
        } else {
          // Finished typing, start pause
          setIsPausing(true)
        }
      } else {
        // Deleting
        if (displayedText === '') {
          setIsDeleting(false)
          setCurrentPlaceholderIndex(prev => (prev + 1) % placeholders.length)
        } else {
          setDisplayedText(currentFullText.substring(0, displayedText.length - 1))
        }
      }
    }

    const timer = setTimeout(handleTyping, speed)

    return () => clearTimeout(timer)
  }, [
    displayedText,
    isDeleting,
    isPausing,
    placeholders,
    currentPlaceholderIndex,
    typingSpeed,
    deletingSpeed,
    pauseDuration
  ])

  return displayedText
}
